import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { IconButton, Spinner, Chip, Input, Button, Tooltip } from '@material-tailwind/react'
import { FiRefreshCw } from 'react-icons/fi'
import { visitServices } from '@/services/visit-services'
import { GroupCard } from './group-card'
import { HeaderMobile } from './header-mobile'
import { HeaderDesktop } from './header-desktop'
import { fmt, norm } from './utils'

export function VisitApprovals() {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const [selectedGroups, setSelectedGroups] = useState({})

  const refresh = useCallback(async () => {
    setError(''); setLoading(true)
    try {
      const list = await visitServices.listPending()
      setRows(Array.isArray(list) ? list : [])
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Error al cargar pendientes')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r => {
      const vd = r?.visitor_data || {}
      return [
        vd.name, vd.email, vd.document_number, vd.host_person, vd.token_name,
        vd.origin_company, vd.company_name, r.office_name, r.office_id
      ].some(x => String(x ?? '').toLowerCase().includes(q))
    })
  }, [rows, query])

  const now = Date.now()
  const notExpired = useMemo(
    () => filtered.filter(r => !r?.ends_at || new Date(r.ends_at).getTime() >= now),
    [filtered, now]
  )

  const groups = useMemo(() => {
    const map = new Map()
    for (const r of notExpired) {
      const key = r.group_id || '—'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(r)
    }
    return Array.from(map.entries())
      .map(([group_id, items]) => {
        const office_id = items[0]?.office_id ?? null
        const office_name = items[0]?.office_name ?? null
        const created_at = items[0]?.created_at ?? null

        const ends = items.map(i => i?.ends_at).filter(Boolean)
        const maxEnd = ends.length ? new Date(Math.max(...ends.map(d => new Date(d).getTime()))) : null

        const starts = items.map(i => i?.starts_at).filter(Boolean)
        const minStart = starts.length ? new Date(Math.min(...starts.map(d => new Date(d).getTime()))) : null

        const window_text = (minStart || maxEnd)
          ? `${fmt(minStart)} – ${fmt(maxEnd)}`
          : '—'

        return {
          group_id, office_id, office_name, created_at, window_text,
          expired: maxEnd ? (maxEnd.getTime() < now) : false,
          count: items.length, items
        }
      })
      .sort((a, b) => (new Date(b.created_at) - new Date(a.created_at)))
  }, [notExpired, now])

  const toggleGroup = (gid) => setSelectedGroups(s => ({ ...s, [gid]: !s[gid] }))
  const clearSelection = () => setSelectedGroups({})
  const selectAll = () => {
    const all = {}
    for (const g of groups) if (!g.expired && g.count > 0) all[g.group_id] = true
    setSelectedGroups(all)
  }
  const selectedIds = useMemo(
    () => Object.entries(selectedGroups).filter(([, v]) => v).map(([k]) => k),
    [selectedGroups]
  )
  const selectableGroupIds = useMemo(
    () => groups.filter(g => !g.expired && g.count > 0).map(g => g.group_id),
    [groups]
  )
  const allSelected = selectableGroupIds.length > 0 && selectableGroupIds.every(id => !!selectedGroups[id])
  const someSelected = selectedIds.length > 0 && !allSelected

  const masterRef = useRef(null)
  const masterRefMobile = useRef(null)
  useEffect(() => { [masterRef.current, masterRefMobile.current].forEach(el => { if (el) el.indeterminate = someSelected }) }, [someSelected])
  const onMasterToggle = () => { if (allSelected || someSelected) clearSelection(); else selectAll() }

  const doApprove = async (gid) => {
    setError('')
    try { await visitServices.bulkApprove({ group_ids: [gid] }); await refresh(); setSelectedGroups(s => ({ ...s, [gid]: false })) }
    catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudo aprobar el bloque') }
  }
  const doReject = async (gid) => {
    setError('')
    try { await visitServices.bulkReject({ group_ids: [gid] }); await refresh(); setSelectedGroups(s => ({ ...s, [gid]: false })) }
    catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudo rechazar el bloque') }
  }
  const doApproveOne = async (id) => { setError(''); try { await visitServices.approveInvitation(id); await refresh() } catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudo aprobar la invitación') } }
  const doRejectOne = async (id) => { setError(''); try { await visitServices.rejectInvitation(id); await refresh() } catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudo rechazar la invitación') } }
  const approveSelected = async () => {
    if (selectedIds.length === 0) return
    setError('')
    try { await visitServices.bulkApprove({ group_ids: selectedIds }); await refresh(); clearSelection() }
    catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudieron aprobar los bloques seleccionados') }
  }
  const rejectSelected = async () => {
    if (selectedIds.length === 0) return
    setError('')
    try { await visitServices.bulkReject({ group_ids: selectedIds }); await refresh(); clearSelection() }
    catch (e) { setError(e?.response?.data?.error || e.message || 'No se pudieron rechazar los bloques seleccionados') }
  }

  return (
    <div className="space-y-4">
      {/* Header móvil */}
      <HeaderMobile
        loading={loading}
        rowsCount={rows.length}
        query={query}
        setQuery={setQuery}
        masterRef={masterRefMobile}
        allSelected={allSelected}
        someSelected={someSelected}
        onMasterToggle={onMasterToggle}
        onRefresh={refresh}
        onApproveSelected={approveSelected}
        onRejectSelected={rejectSelected}
      />

      {/* Header desktop */}
      <HeaderDesktop
        loading={loading}
        rowsCount={rows.length}
        query={query}
        setQuery={setQuery}
        masterRef={masterRef}
        allSelected={allSelected}
        someSelected={someSelected}
        onMasterToggle={onMasterToggle}
        onRefresh={refresh}
        onApproveSelected={approveSelected}
        onRejectSelected={rejectSelected}
      />

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">{error}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600"><Spinner className="h-4 w-4" /> Cargando…</div>
      ) : groups.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg p-3">No hay invitaciones pendientes por aprobar.</div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => (
            <GroupCard
              key={g.group_id || Math.random()}
              group={g}
              selected={selectedGroups}
              toggleGroup={toggleGroup}
              onApprove={doApprove}
              onReject={doReject}
              onApproveOne={doApproveOne}
              onRejectOne={doRejectOne}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default VisitApprovals